import {
  detailsPLSchedules,
  investmentDetailsPLSchedules,
} from "../../../../utils/static/detailsPL";

export const ROW_TYPE_OPTIONS = {
  Rental: [
    { value: "rental_gross_income", label: "Gross Income" },
    { value: "rental_expenses", label: "Expenses" },
  ],
  "Medical Expense": [
    {
      value: "medical_employee_remuneration",
      label: "Employee Remuneration",
    },
    { value: "medical_expenses", label: "Medical Expense" },
  ],
  "Other Income": [
    { value: "other_income", label: "Other Income" },
    { value: "other_income_expenses", label: "Direct Expenses" },
  ],
  "Other Investment Income": [
    { value: "other_investment_income", label: "Other Investment Income" },
    { value: "other_investment_income_expenses", label: "Direct Expenses" },
  ],
  "Service Income": [
    { value: "service_income", label: "Service Income" },
    { value: "service_income_expenses", label: "Service Expenses" },
  ],
  "Foreign Trade": [
    { value: "foreign_trade_revenue", label: "Revenue" },
    { value: "foreign_trade_expenses", label: "Expenses" },
  ],
  "Allocation Of Expenses": [
    {
      value: "allocation_of_expenses_statutory",
      label: "Statutory Expenses",
    },
    { value: "allocation_of_expenses_common", label: "Common Expenses" },
  ],
};

export const AUTO_TAGGING_TYPES = ["Allocation Of Expenses"];

export function resolveScheduleType(type, companyType = "common") {
  return companyType === "investment"
    ? investmentDetailsPLSchedules[type]
    : detailsPLSchedules[type];
}

export function canAutoTag(type) {
  return AUTO_TAGGING_TYPES.includes(type);
}

export function isRowTypeRequired(
  selectedScheduleType,
  companyType = "common"
) {
  return Boolean(
    ROW_TYPE_OPTIONS[resolveScheduleType(selectedScheduleType, companyType)]
  );
}

export function getInitialScheduleState(row, editMode = false) {
  let scheduleType = row?.taxAdjustment || "";
  let initialRowType = "";
  let scheduleTitle = "";
  let isRowTypeDisabled = false;

  if (row?.taxAdjustment === "Allocation of Expenses/Common Expenses") {
    scheduleType = "Allocation Of Expenses";
    initialRowType = "allocation_of_expenses_common";
    scheduleTitle = "Allocation Of Expenses";
    isRowTypeDisabled = true;
  } else if (
    row?.taxAdjustment === "Allocation of Expenses/Statutory Expenses"
  ) {
    scheduleType = "Allocation Of Expenses";
    initialRowType = "allocation_of_expenses_statutory";
    scheduleTitle = "Allocation Of Expenses";
    isRowTypeDisabled = true;
  } else if (row?.taxAdjustment === "Renovation Schedule") {
    scheduleTitle = "Renovation and Refurbishment Deductions Under S14N";
  }

  if (editMode && row?.schedule) {
    return {
      scheduleType,
      selectedSchedule: {
        sch_id: row.schedule.sch_id,
        scheduleTitle: row.schedule.scheduleTitle,
        scheduleNo: row.schedule.scheduleNo,
      },
      targetSchedule: {
        scheduleTitle: row.schedule.scheduleTitle,
        rowType: isRowTypeDisabled
          ? initialRowType
          : row.schedule.rowType || initialRowType,
      },
      isRowTypeDisabled,
    };
  }

  return {
    scheduleType,
    selectedSchedule: {
      sch_id: null,
      scheduleTitle: "",
      scheduleNo: null,
    },
    targetSchedule: {
      scheduleTitle,
      rowType: initialRowType,
    },
    isRowTypeDisabled,
  };
}

export function createRowMap(
  row,
  rowIndex,
  section,
  selectedScheduleType,
  companyType = "common"
) {
  const base = {
    row_id: row.row_id,
    description: row.name,
    amount:
      companyType === "investment"
        ? Number(row.amount)
        : Math.abs(Number(row.amount)),
    reference: "",
    source: {
      original_amount: row.amount,
      section,
      index: rowIndex,
    },
  };

  const scheduleType = resolveScheduleType(selectedScheduleType, companyType);
  const rowTypes = {
    "Investment Savy": { ...base, name: row.name, sch: "P&L" },
    Savy: { ...base, name: row.name, sch: "P&L" },
    "Partial Breakdown": { ...base, taxAdjustment: "" },
    "Full Breakdown": { ...base, taxAdjustment: "" },
    Rental: { ...base, adjustment: "Direct" },
    Donation: { ...base, approvedIPC: "", details: "" },
    "Interest Expense": { ...base, isIncomeProducing: false },
    "Other Income": { ...base, adjustment: "Direct" },
    "Other Investment Income": { ...base, adjustment: "Direct" },
    "Service Income": { ...base, adjustment: "Direct" },
    "Foreign Trade": { ...base, adjustment: "Direct" },
    "Allocation Of Expenses": { ...base },
    "Medical Expense": base,
    "Further Deduction": base,
    "Share Based Payment": { ...base, taxAdjustment: "" },
    Movement: { ...base, taxAdjustment: "" },
    Renovation: base,
    FAAdditions: {
      row_id: row.row_id,
      description: row.name,
      cost: Math.abs(Number(row.amount)),
      reference: "",
      isDeferred: false,
      CaName: "",
      source: {
        original_amount: row.amount,
        section,
        index: rowIndex,
      },
    },
  };

  return { [scheduleType]: [rowTypes[scheduleType]] };
}

export function createDataMap(
  tcId,
  scheduleId,
  sourceId,
  selectedScheduleType,
  targetSchedule,
  reference,
  companyType = "common"
) {
  const scheduleType = resolveScheduleType(selectedScheduleType, companyType);
  const rowTypes = {
    "Investment Savy": "savy_rows",
    Savy: "savy_rows",
    "Partial Breakdown": "source_rows",
    "Full Breakdown": "source_rows",
    Rental: "",
    Donation: "donation_rows",
    "Interest Expense": "interest_expenses",
    "Other Income": "",
    "Other Investment Income": "",
    "Service Income": "",
    "Foreign Trade": "",
    "Medical Expense": "",
    "Further Deduction": "further_deduction_rows",
    "Share Based Payment": "source_rows",
    "Allocation Of Expenses": "",
    Movement: "movement_rows",
    Renovation: "renovation_rows",
    FAAdditions: "fa_additions_other_source",
  };

  if (!(scheduleType in rowTypes)) return {};

  return {
    tc_id: tcId,
    sch_id: scheduleId,
    sourceType: reference === "P&L" ? "p_l" : "pl_schedule",
    source_id: sourceId,
    refDoc: scheduleType === "FAAdditions" ? "ca_schedule" : "pl_schedule",
    rowType: rowTypes[scheduleType] || targetSchedule.rowType,
  };
}

export function buildSchedulePayload({
  tcId,
  detailsId,
  scheduleId = null,
  row,
  rowIndex,
  section,
  selectedScheduleType,
  targetSchedule,
  reference,
  companyType,
}) {
  const scheduleType = resolveScheduleType(selectedScheduleType, companyType);
  const rowMap = createRowMap(
    row,
    rowIndex,
    section,
    selectedScheduleType,
    companyType
  );
  const dataMap = createDataMap(
    tcId,
    scheduleId,
    detailsId,
    selectedScheduleType,
    targetSchedule,
    reference,
    companyType
  );

  return {
    rowMap,
    dataMap,
    scheduleType,
    refObj: {
      row,
      section,
      index: rowIndex,
      scheduleType,
      adjustment: dataMap,
      refSource: { section, index: rowIndex },
    },
  };
}
